import type { PlayerSide } from './types';

export type VoiceCue = 'intro' | 'win' | 'lose' | 'undo';

interface AudioPreferences {
  music: boolean;
  sound: boolean;
  voice: boolean;
}

const AUDIO_KEY = 'cloud-gomoku-audio-v1';
const VOICE_VERSION = 'qwen-voicedesign-v1-20260905';
const DEFAULTS: AudioPreferences = { music: true, sound: true, voice: true };

export class AudioDirector {
  private context?: AudioContext;
  private musicGain?: GainNode;
  private musicTimer = 0;
  private musicStep = 0;
  private musicTheme = 0;
  private voice?: HTMLAudioElement;
  private finishVoice?: () => void;
  private preferences: AudioPreferences = { ...DEFAULTS };

  constructor() {
    try {
      this.preferences = { ...DEFAULTS, ...JSON.parse(localStorage.getItem(AUDIO_KEY) ?? '{}') };
    } catch {
      localStorage.removeItem(AUDIO_KEY);
    }
  }

  get prefs(): Readonly<AudioPreferences> {
    return this.preferences;
  }

  private save(): void {
    localStorage.setItem(AUDIO_KEY, JSON.stringify(this.preferences));
  }

  async unlock(): Promise<void> {
    if (!this.context) this.context = new AudioContext();
    if (this.context.state === 'suspended') await this.context.resume();
    if (this.preferences.music && !this.musicTimer) this.startMusic();
  }

  async toggle(kind: keyof AudioPreferences): Promise<void> {
    this.preferences[kind] = !this.preferences[kind];
    this.save();
    await this.unlock();
    if (kind === 'music') {
      if (this.preferences.music) this.startMusic();
      else this.stopMusic();
    }
    if (kind === 'voice' && !this.preferences.voice) this.stopVoice();
    if (kind !== 'sound') this.playUi();
  }

  playUi(): void {
    if (!this.preferences.sound) return;
    void this.unlock().then(() => this.tone(520, 0.035, 0.03, 'sine'));
  }

  playStone(side: PlayerSide): void {
    if (!this.preferences.sound) return;
    void this.unlock().then(() => {
      const ctx = this.context!;
      const now = ctx.currentTime;
      const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.065), ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (data.length * 0.15));
      const noise = ctx.createBufferSource();
      const filter = ctx.createBiquadFilter();
      const gain = ctx.createGain();
      noise.buffer = buffer;
      filter.type = 'bandpass';
      filter.frequency.value = side === 1 ? 520 : 720;
      filter.Q.value = 1.4;
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.065);
      noise.connect(filter).connect(gain).connect(ctx.destination);
      noise.start(now);
      this.tone(side === 1 ? 145 : 196, 0.085, 0.08, 'sine');
    });
  }

  playOutcome(humanWon: boolean): void {
    if (!this.preferences.sound) return;
    void this.unlock().then(() => {
      const notes = humanWon ? [392, 494, 587, 784] : [294, 247, 196];
      notes.forEach((frequency, index) => window.setTimeout(() => this.tone(frequency, 0.3, 0.075, 'triangle'), index * 130));
    });
  }

  enableVoice(): void {
    if (this.preferences.voice) return;
    this.preferences.voice = true;
    this.save();
  }

  stopVoice(): void {
    this.voice?.pause();
    this.finishVoice?.();
    this.finishVoice = undefined;
    this.voice = undefined;
  }

  playVoice(characterId: string, cue: VoiceCue): Promise<void> {
    return this.playVoiceUrl(`/audio/voices/${VOICE_VERSION}-${characterId}-${cue}.mp3`);
  }

  playVoicePreview(characterId: string): Promise<void> {
    return this.playVoiceUrl(`/audio/voice-previews/${VOICE_VERSION}-${characterId}-intro.mp3`);
  }

  private playVoiceUrl(url: string): Promise<void> {
    if (!this.preferences.voice) return Promise.resolve();
    this.stopVoice();
    this.voice = new Audio(url);
    this.voice.volume = 0.9;
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        this.finishVoice = undefined;
        resolve();
      };
      this.finishVoice = finish;
      this.voice!.addEventListener('ended', finish, { once: true });
      this.voice!.addEventListener('error', finish, { once: true });
      void this.voice!.play().catch(finish);
    });
  }

  private tone(frequency: number, duration: number, volume: number, type: OscillatorType): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  private startMusic(): void {
    if (!this.context || this.musicTimer || !this.preferences.music) return;
    this.musicGain = this.context.createGain();
    this.musicGain.gain.value = 0.42;
    this.musicGain.connect(this.context.destination);
    const themes = [
      { scale: [220, 247, 294, 330, 392, 440], phrase: [0, 2, 4, 3, 1, 4, 2, 5], upper: 'triangle' as OscillatorType },
      { scale: [196, 220, 262, 294, 330, 392], phrase: [0, 3, 2, 5, 4, 1, 3, 2], upper: 'sine' as OscillatorType },
      { scale: [247, 294, 330, 370, 440, 494], phrase: [0, 1, 4, 2, 5, 3, 1, 4], upper: 'triangle' as OscillatorType },
    ];
    const playPhrase = () => {
      if (!this.context || !this.musicGain || !this.preferences.music) return;
      if (this.musicStep > 0 && this.musicStep % 8 === 0) this.musicTheme = (this.musicTheme + 1 + Math.floor(Math.random() * (themes.length - 1))) % themes.length;
      const theme = themes[this.musicTheme];
      const root = theme.scale[theme.phrase[this.musicStep % theme.phrase.length]];
      const now = this.context.currentTime;
      [root / 2, root, root * 1.5].forEach((frequency, index) => {
        const oscillator = this.context!.createOscillator();
        const gain = this.context!.createGain();
        oscillator.type = index === 0 ? 'sine' : theme.upper;
        oscillator.frequency.value = frequency;
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(index === 0 ? 0.025 : 0.012, now + 0.35);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.9);
        oscillator.connect(gain).connect(this.musicGain!);
        oscillator.start(now);
        oscillator.stop(now + 3);
      });
      this.musicStep += 1;
    };
    playPhrase();
    this.musicTimer = window.setInterval(playPhrase, 2800);
  }

  private stopMusic(): void {
    window.clearInterval(this.musicTimer);
    this.musicTimer = 0;
    this.musicGain?.disconnect();
    this.musicGain = undefined;
  }
}
